import React, { useState } from 'react';
import {
    Layout, Button, Card, Space, Tag, Typography, Table, Tooltip, Popconfirm,
    Input, Select, Form, message, ConfigProvider
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, ReloadOutlined, SettingOutlined
} from '@ant-design/icons';
import zhCN from 'antd/lib/locale/zh_CN';
import { MOCK_APPS, INIT_PACKAGES, REGION_MAP, INIT_REGION_PRICING_TEMPLATES } from './mockData';
import { getCountryLabel, formatUsdFen, hasRegionPricing } from '../../../utils/regionPricing';
import PackageDrawer from './components/PackageDrawer';
import RegionPricingTemplateManageDialog from './components/RegionPricingTemplateManageDialog';

const { Content } = Layout;
const { Text } = Typography;

// ══════════════════════════════════════════════
//  套餐配置列表
//  真实项目：views/packageManage/packageConfig/index.vue
// ══════════════════════════════════════════════

const AppPackage = () => {
    const [packages, setPackages] = useState(INIT_PACKAGES);
    const [searchForm] = Form.useForm();
    const [query, setQuery] = useState({});
    const [drawer, setDrawer] = useState({ open: false, editing: null });
    const [regionTemplates, setRegionTemplates] = useState(INIT_REGION_PRICING_TEMPLATES);
    const [tplManageOpen, setTplManageOpen] = useState(false);

    const handleSearch = () => setQuery(searchForm.getFieldsValue());
    const handleReset = () => { searchForm.resetFields(); setQuery({}); };

    const filtered = packages.filter(p => {
        if (query.name && !p.name.toLowerCase().includes(query.name.trim().toLowerCase())) return false;
        if (query.packageId && !p.packageId.toLowerCase().includes(query.packageId.trim().toLowerCase())) return false;
        if (query.applicationDirection && p.applicationDirection !== query.applicationDirection) return false;
        if (query.releaseArea && !(p.releaseArea || []).includes(query.releaseArea)) return false;
        if (query.releaseApp) {
            const appName = MOCK_APPS.find(a => a.appId === query.releaseApp)?.appName;
            if (!(p.releaseAppList || []).some(a => a.appName === appName)) return false;
        }
        return true;
    });

    const handleSave = (payload) => {
        setPackages(prev => {
            const exists = prev.some(p => p.packageId === payload.packageId);
            return exists ? prev.map(p => (p.packageId === payload.packageId ? payload : p)) : [payload, ...prev];
        });
        setDrawer({ open: false, editing: null });
        message.success(drawer.editing ? '编辑成功' : '创建成功');
    };

    const handleDelete = (record) => {
        setPackages(prev => prev.filter(p => p.packageId !== record.packageId));
        message.success('删除成功');
    };

    const columns = [
        {
            title: '套餐信息', key: 'package', width: 220, fixed: 'left',
            render: (_, r) => (
                <div>
                    <div style={{ fontWeight: 500 }}>{r.name}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, marginTop: 4 }}>
                        <span style={{ color: '#bfbfbf', fontSize: 12 }}>{r.packageId}</span>
                        {(r.packageLabel || []).map(label => <Tag key={label}>{label}</Tag>)}
                    </div>
                </div>
            ),
        },
        { title: '应用方向', key: 'applicationDirection', width: 90, align: 'center', render: (_, r) => (r.applicationDirection === 1 ? '设备' : '用户') },
        {
            title: '投放区域', key: 'releaseArea', width: 140,
            render: (_, r) => (r.releaseArea || []).length
                ? (r.releaseArea || []).map(item => <Tag key={item} style={{ marginBottom: 2 }}>{REGION_MAP[item] || item}</Tag>)
                : '-',
        },
        {
            title: '投放应用', key: 'releaseApp', width: 220,
            render: (_, r) => (r.releaseAppList || []).length ? (
                <Tooltip title={(r.releaseAppList || []).map(a => `${a.appName}(${a.appCount})`).join('、')}>
                    <span style={{ display: 'inline-block', maxWidth: 210, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                        {(r.releaseAppList || []).map(a => `${a.appName}(${a.appCount})`).join('、')}
                    </span>
                </Tooltip>
            ) : '-',
        },
        {
            title: '投放运营位', key: 'releaseOpsPosition', width: 140, align: 'center',
            render: (_, r) => (r.releaseOpsPosition || []).length
                ? (r.releaseOpsPosition || []).map(item => <Tag key={item} color="warning" style={{ marginBottom: 2 }}>{item}</Tag>)
                : '-',
        },
        {
            title: '订阅类型', key: 'subscriptionType', width: 140, align: 'center',
            render: (_, r) => {
                if (r.isFree === 1) return <Tag color="success">免费套餐</Tag>;
                if (r.isSubscription === 1 || r.isNoSubscription === 1) {
                    return (
                        <Space size={4} wrap>
                            {r.isSubscription === 1 && <Tag color="blue">订阅制</Tag>}
                            {r.isNoSubscription === 1 && <Tag color="warning">非订阅</Tag>}
                        </Space>
                    );
                }
                return '-';
            },
        },
        {
            title: '套餐底价', key: 'platformCost', width: 120,
            render: (_, r) => (r.platformCostCny == null && r.platformCostUsd == null) ? '-' : (
                <div>
                    {r.platformCostCny != null && <div>¥ {(r.platformCostCny / 100).toFixed(2)}</div>}
                    {r.platformCostUsd != null && <div style={{ color: '#8c8c8c' }}>$ {(r.platformCostUsd / 100).toFixed(2)}</div>}
                </div>
            ),
        },
        {
            title: '售价（国内）', key: 'salePrice', width: 140,
            render: (_, r) => {
                if (r.isFree === 1) return <span style={{ fontSize: 12, color: '#bfbfbf' }}>免费</span>;
                if (r.isSubscription !== 1 && r.isNoSubscription !== 1) return '-';
                return (
                    <div>
                        {r.isSubscription === 1 && (
                            <div>
                                <span style={{ fontSize: 12, color: '#bfbfbf' }}>订阅 </span>
                                {r.salePriceCny != null && <span style={{ color: '#ff4d4f' }}>¥ {(r.salePriceCny / 100).toFixed(2)}</span>}
                            </div>
                        )}
                        {r.isNoSubscription === 1 && (
                            <div>
                                <span style={{ fontSize: 12, color: '#bfbfbf' }}>非订阅 </span>
                                {r.noSubscriptionPriceCny != null && <span style={{ color: '#ff4d4f' }}>¥ {(r.noSubscriptionPriceCny / 100).toFixed(2)}</span>}
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: '区域定价（海外）', key: 'regionPricing', width: 200,
            render: (_, r) => {
                if (r.isFree === 1) return <span style={{ fontSize: 12, color: '#bfbfbf' }}>免费</span>;
                const list = [];
                if (r.isSubscription === 1) list.push({ label: '订阅', rp: r.subscriptionRegionPricing });
                if (r.isNoSubscription === 1) list.push({ label: '非订阅', rp: r.noSubscriptionRegionPricing });
                if (list.length === 0) return '-';
                return (
                    <div>
                        {list.map(({ label, rp }) => {
                            const countries = (rp?.countryOverrides || []).filter(c => c && c.country);
                            return (
                                <div key={label} style={{ marginBottom: 2 }}>
                                    <span style={{ fontSize: 12, color: '#bfbfbf' }}>{label} </span>
                                    {!hasRegionPricing(rp)
                                        ? <Text type="secondary" style={{ fontSize: 12 }}>未配置</Text>
                                        : (
                                            <Tooltip title={countries.length
                                                ? countries.map(c => `${getCountryLabel(c.country)} ${formatUsdFen(c.usdPrice)}`).join('、')
                                                : '未设置国家覆盖'}>
                                                <span style={{ color: '#8c8c8c' }}>
                                                    默认 {formatUsdFen(rp.defaultUsd)}{countries.length ? ` · ${countries.length} 国覆盖` : ''}
                                                </span>
                                            </Tooltip>
                                        )}
                                </div>
                            );
                        })}
                    </div>
                );
            },
        },
        {
            title: '创建时间', key: 'createdAt', width: 175,
            render: (_, r) => (r.createdAt ? new Date(r.createdAt * 1000).toLocaleString('zh-CN') : '-'),
        },
        {
            title: '操作', key: 'action', width: 160, align: 'center', fixed: 'right',
            render: (_, r) => (
                <Space size={8}>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => setDrawer({ open: true, editing: r })}>编辑</Button>
                    <Popconfirm title="确定删除该套餐？删除后不可恢复。" okText="确定" cancelText="取消" okButtonProps={{ danger: true }} onConfirm={() => handleDelete(r)}>
                        <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <ConfigProvider locale={zhCN}>
            <Layout style={{ minHeight: 'calc(100vh - 64px)', background: '#f5f6f8' }}>
                <Content style={{ padding: 24 }}>
                    <Card>
                        {/* 搜索区 */}
                        <Form form={searchForm} layout="inline" style={{ rowGap: 12, marginBottom: 16 }} onFinish={handleSearch}>
                            <Form.Item label="套餐名称" name="name" style={{ marginBottom: 0 }}>
                                <Input placeholder="搜索套餐名称" allowClear style={{ width: 160 }} />
                            </Form.Item>
                            <Form.Item label="套餐ID" name="packageId" style={{ marginBottom: 0 }}>
                                <Input placeholder="搜索套餐ID" allowClear style={{ width: 160 }} />
                            </Form.Item>
                            <Form.Item label="应用方向" name="applicationDirection" style={{ marginBottom: 0 }}>
                                <Select placeholder="全部" allowClear style={{ width: 120 }} options={[{ label: '设备', value: 1 }, { label: '用户', value: 2 }]} />
                            </Form.Item>
                            <Form.Item label="投放区域" name="releaseArea" style={{ marginBottom: 0 }}>
                                <Select placeholder="全部" allowClear style={{ width: 120 }} options={[{ label: '国内', value: '1' }, { label: '海外', value: '2' }]} />
                            </Form.Item>
                            <Form.Item label="选择投放应用" name="releaseApp" style={{ marginBottom: 0 }}>
                                <Select placeholder="全部" allowClear showSearch optionFilterProp="label" style={{ width: 200 }} options={MOCK_APPS.map(a => ({ label: a.appName, value: a.appId }))} />
                            </Form.Item>
                            <Form.Item style={{ marginBottom: 0 }}>
                                <Space>
                                    <Button type="primary" icon={<SearchOutlined />} htmlType="submit">查询</Button>
                                    <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
                                </Space>
                            </Form.Item>
                        </Form>

                        <div style={{ marginBottom: 16 }}>
                            <Space>
                                <Button type="primary" icon={<PlusOutlined />} onClick={() => setDrawer({ open: true, editing: null })}>创建套餐</Button>
                                <Button icon={<SettingOutlined />} onClick={() => setTplManageOpen(true)}>区域定价模板管理</Button>
                            </Space>
                        </div>

                        <Table
                            dataSource={filtered}
                            columns={columns}
                            rowKey="packageId"
                            bordered={false}
                            scroll={{ x: 1780 }}
                            pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }}
                            locale={{ emptyText: <Text type="secondary">暂无套餐，点击上方「创建套餐」</Text> }}
                        />
                    </Card>
                </Content>
            </Layout>

            <PackageDrawer
                open={drawer.open}
                editData={drawer.editing}
                onClose={() => setDrawer({ open: false, editing: null })}
                onSave={handleSave}
                regionTemplates={regionTemplates}
                onRegionTemplatesChange={setRegionTemplates}
                onManageTemplates={() => setTplManageOpen(true)}
            />

            <RegionPricingTemplateManageDialog
                open={tplManageOpen}
                templates={regionTemplates}
                onChange={setRegionTemplates}
                onCancel={() => setTplManageOpen(false)}
            />
        </ConfigProvider>
    );
};

export default AppPackage;
